import { useEffect, useState, useRef } from "react";
import { ReactSVG } from 'react-svg';
import html2pdf from "html2pdf.js";
import { useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchNotepadByService, fetchNotepad, sendEmailWithPdf, uploadPdf } from "../../../../../redux/modules/patient/actions";
import { displayToast } from "../../../../../redux/modules/toast/actions";
import ConfirmationModalNew from '../../../../common/ConfirmationModalNew';
import { InitialAssessmentForm } from "../../../../forms/allergic-rhinitis-forms/InitialAssessmentForm";
import { PhysicianReferralForm } from "../../../../forms/allergic-rhinitis-forms/PhysicianReferralForm";
import { PrescribingRecordForm } from "../../../../forms/allergic-rhinitis-forms/PrescribingRecordForm";
import { CommunicationForBillingForm } from "../../../../forms/allergic-rhinitis-forms/CommunicationForBillingForm";
import { PrescriptionForm } from "../../../../forms/allergic-rhinitis-forms/PrescriptionForm";
import { Storyboard } from "../../../../forms/allergic-rhinitis-forms/storyboard/Storyboard";
import { FormContextMA } from "../../../../forms/form-context";
import { AllergicRhinitisDocTypes, GetReferralDetails } from "../../service-steps/documentation/data";
import { PatientReferralForm } from "../../../../forms/allergic-rhinitis-forms/PatientReferralForm";
import { NotepadFormMA } from "../../../../forms/allergic-rhinitis-forms/NotepadFormMA";
import { filterNotesByServiceDate } from "../../../../../helpers/functions";
import { getPharmacyInfo } from "../../../../forms/common/getPharmacyInfo";

const DocumentationForm = ({clinicalServiceObj,
  clinicalServiceName,
  patient,
  formType,
  onBack = () => {},}) => {
  
  const dispatch = useDispatch();
  const { id: patientId, serviceId } = useParams();
  const { formsNotepad, notepad } = useSelector((state) => state.patients);
  const location = useLocation();
  const { currentLoggedInUserDetail: userFromCurrent } = useSelector((state) => state.currentLoggedInUserDetail ?? {});
  const { currentLoggedInUserDetail: userFromUsers, pharmacyStores } = useSelector((state) => state.users ?? {});
  const pharmacy = getPharmacyInfo(userFromCurrent ?? userFromUsers, pharmacyStores) ?? {};
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [printMode, setPrintMode] = useState("bw");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const printButtonRef = useRef(null);
  const printableRef = useRef(null);

  const notepadFormTypes = [
    AllergicRhinitisDocTypes.Notepad,
    AllergicRhinitisDocTypes.NotepadPharmacist,
    AllergicRhinitisDocTypes.NotepadPatient
  ];

  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (serviceId && clinicalServiceName) {
      dispatch(fetchNotepadByService(clinicalServiceName, serviceId));
    }
  }, [serviceId, clinicalServiceName, dispatch]);

  useEffect(() => {
    if (!notepadFormTypes.includes(formType)) return;

    if (serviceId && clinicalServiceName) {
      dispatch(fetchNotepadByService(clinicalServiceName, serviceId));
    } else if (patientId) {
      dispatch(fetchNotepad(patientId));
    }
  }, [formType, patientId, serviceId, clinicalServiceName, dispatch]);

  function handlePrint() {
    
    if (!printableRef.current) {
      window.print();
      return;
    }
    const html = printableRef.current.innerHTML;
    const newWin = window.open('', '_blank');
    if (!newWin) {
      // popup blocked — fallback to default print
      window.print();
      return;
    }
    // copy stylesheet links to new window to preserve styles
    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map((l) => l.outerHTML)
      .join('\n');
    const styles = Array.from(document.querySelectorAll('style'))
      .map((s) => s.outerHTML)
      .join('\n');

    newWin.document.open();
    newWin.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Print</title>${links}${styles}<style>@page{size: letter portrait; margin: 0.5in;} body{margin:0; padding:0; -webkit-print-color-adjust:exact; print-color-adjust:exact;} .printable-area{width:7.5in; margin:0 auto;} .printable-area *{max-height:none !important;} </style></head><body>`);
    newWin.document.write(`<div class="print-container printable-area">${html}</div>`);
    newWin.document.write('</body></html>');
    newWin.document.close();
    newWin.focus();
    // give browser a moment to render styles
    setTimeout(() => {
      try {
        newWin.print();
      } catch (err) {
        console.error('Print failed', err);
      }
      // close the window after printing
      try { newWin.close(); } catch (e) {}
    }, 500);
  }

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  async function handleSendEmail() {
    const normalizedEmail = (email || "").trim();
    if (!normalizedEmail) {
      setEmailError("Please enter recipient email");
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!printableRef.current) {
      setEmailError("Document is not ready to send yet");
      return;
    }

    try {
      setLoading(true);
      setEmailError("");

      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          filename: `${clinicalServiceName || "document"}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        })
        .from(printableRef.current)
        .outputPdf("blob");

      const pdfFilename = `${clinicalServiceName || "document"}.pdf`;
      const pdfLink = await dispatch(uploadPdf(pdfBlob, pdfFilename));

      const formData = new FormData();
      formData.append("File", pdfBlob, pdfFilename);
      formData.append("ToEmail", normalizedEmail);
      formData.append("Subject", clinicalServiceName || "Document");
      formData.append("PatientName", `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim());
      formData.append("FormName", clinicalServiceName || "Document");
      formData.append("PharmacyName", pharmacy.pharmacyName || "");
      formData.append("StoreNumber", pharmacy.pharmacyStoreNumber || "");
      formData.append("PhoneNumber", pharmacy.pharmacyPhone || "");
      formData.append("FaxNumber", pharmacy.pharmacyFax || "");
      formData.append("StoreEmail", pharmacy.pharmacyEmail || "");
      formData.append("PdfLink", pdfLink);

      await dispatch(sendEmailWithPdf(formData));
      dispatch(displayToast('Email sent successfully', 'success', { autoClose: 4000 }));
      setShowEmailModal(false);
      setEmail("");
    } catch (error) {
      setEmailError(error?.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  }

  if (!clinicalServiceObj || !clinicalServiceName || !patient) {
    return <div>Loading</div>;
  }

  function handleNextPage() {
    if (currentPage >= totalPages) return;
    setCurrentPage(currentPage + 1);
  }

  function handlePreviousPage() {
    if (currentPage <= 1) return;
    setCurrentPage(currentPage - 1);
  }

  let formComponent = <div>No Form Found!</div>;
  switch (formType) {
    case AllergicRhinitisDocTypes.InitialAssessment:
      formComponent = <InitialAssessmentForm />;
      break;
    case AllergicRhinitisDocTypes.PhysicianReferralForm:
      formComponent = <PhysicianReferralForm />;
      break;
    case AllergicRhinitisDocTypes.PatientReferral:
      formComponent = <PatientReferralForm />;
      break;
    case AllergicRhinitisDocTypes.Prescription:
      formComponent = <PrescriptionForm />;
      break;
    case AllergicRhinitisDocTypes.CommunicationForBilling:
      formComponent = <CommunicationForBillingForm />;
      break;
    case AllergicRhinitisDocTypes.PrescribingRecord:
      formComponent = <PrescribingRecordForm />;
      break;
    case AllergicRhinitisDocTypes.PrescribingRecordWithReferral:
      formComponent = <PrescribingRecordForm showReferral={true} />;
      break;
    case AllergicRhinitisDocTypes.Storyboard:
      formComponent = (
        <Storyboard currentPage={currentPage} setTotalPages={setTotalPages} />
      );
      break;
    case AllergicRhinitisDocTypes.Notepad:
    case AllergicRhinitisDocTypes.NotepadPharmacist:
    case AllergicRhinitisDocTypes.NotepadPatient:
      formComponent = <NotepadFormMA />;
      break;
    default:
      onBack();
      break;
  }

  return (
    <>
    <div className="p-9 flex flex-row gap-6 w-full justify-between items-start">
      <div className="xl:min-w-[180px]">
        <button onClick={() => onBack()} aria-label="Back" className="flex items-center justify-center">
          <ReactSVG
            src="/icons/back-arrow.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-16 h-16');
            }} 
          />
        </button>  
      </div>
      <div className="flex justify-center w-full">
        <div className="relative">
          {currentPage > 1 && (
            <button
              className="absolute left-0 -translate-x-16 top-1/2 -translate-y-1/2 w-10 h-10 bg-gray-100 rounded-full border flex items-center justify-center shadow z-50 print:hidden"
              onClick={handlePreviousPage}
              aria-label="Previous page"
            >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="#0B4C5B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
          )}
          <div className="w-[8.5in] h-[90vh] overflow-y-auto no-scrollbar shadow-lg print:h-auto print:overflow-visible">
            <div className="min-h-[11in] print:min-h-[11in]" ref={printableRef}>
              <FormContextMA.Provider
                value={{
                  formType: formType || "",
                  clinicalServiceName: clinicalServiceName || "",
                  serviceId: serviceId || null,
                  patient: patient || {},
                  preAssessment:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.preAssessment || {},
                  clinicalAssessment:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.clinicalAssessment || {},
                  signsAndSymptoms:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.signsAndSymptoms || {},
                  differentialAssessment:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.differentialAssessment || {},
                  documentation:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.documentation || {},
                  treatment:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.treatment || {},
                  education:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.education || {},
                  startDateTime:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.startDateTime,
                  endDateTime:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.endDateTime,
                  showReferral:
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                      ?.showReferral,
                  alertData: GetReferralDetails(
                    clinicalServiceObj?.initialAssessment?.initialAssessmentForm,
                  ),
                  
                  notepad: (() => {
                    const source = (formsNotepad && formsNotepad.length > 0) ? formsNotepad : (notepad || []);
                    const params = new URLSearchParams(location.search);
                    const serviceDate = params.get('serviceDate');
                    return filterNotesByServiceDate(source, serviceDate);
                  })()
                }}
              >
                {formComponent}
              </FormContextMA.Provider>
            </div>
          </div>
          {currentPage < totalPages && (
            <button
              className="absolute right-0 translate-x-16 top-1/2 -translate-y-1/2 w-10 h-10 bg-gray-100 rounded-full border flex items-center justify-center shadow z-50 print:hidden"
              onClick={handleNextPage}
              aria-label="Next page"
            >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 6L15 12L9 18" stroke="#0B4C5B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
          )}
        </div>
      </div>  
      <div className="flex flex-col xl:flex-row bg-white border border-gray-100 relative">
        <button
          aria-label="Print"
          ref={printButtonRef}
          onClick={() => handlePrint()}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/printer-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">
            Print
          </span>
        </button>

        <div className="hidden xl:block w-px bg-gray-100 self-center h-11" />
        <button
          aria-label="Email"
          onClick={() => {
            setEmailError("");
            setShowEmailModal(true);
          }}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/mail-open.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">
            Email
          </span>
        </button>

        <div className="hidden xl:block w-px bg-gray-100 self-center h-11" />
        <button aria-label="Fax" className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20">
          <ReactSVG
            src="/icons/fax-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-[85%] hidden group-hover:block bg-gray-20 text-gray-800 shadow rounded py-2 px-4 whitespace-nowrap">
            <span className="font-semibold">Email Fax</span> - Coming Soon
          </span>
        </button>
      </div>
      {/* Previous/Next chevrons are positioned near the document (absolute) */}
    </div>
    <ConfirmationModalNew
      open={showEmailModal}
      title={"Send Email"}
      className={"!w-[400px] !max-w-[400px] !p-0"}
      content={() => (
        <div className="p-2">
          <input
            type="email"
            placeholder="Enter recipient email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            className="w-full border border-gray-300 p-2 rounded mb-2"
          />
          {emailError && <p className="text-sm text-red-600 mb-3">{emailError}</p>}
        </div>
      )}
      handleClose={() => { setShowEmailModal(false); setEmailError(""); }}
      onCross={() => { setShowEmailModal(false); setEmailError(""); }}
      confirmText={loading ? "Sending..." : "Send"}
      cancelText={"Cancel"}
      confirmAction={handleSendEmail}
      cancelAction={() => { setShowEmailModal(false); setEmailError(""); }}
      disableConfirm={loading}
      hideCloseIcon={false}
    />
    </>
  );
};

export default DocumentationForm;
